import React, { useCallback, useEffect, useRef, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { useTranscription } from "../../hooks/useTranscription";
import {
  JobItem,
  TranscriptionResponse,
  TranscriptionService,
  TranscriptSegment,
} from "../../services/transcription.service";

const SPEAKER_COLOURS: Record<string, string> = {
  SPEAKER_00: "#d4e6f1",
  SPEAKER_01: "#d5f5e3",
  SPEAKER_02: "#fdebd0",
  SPEAKER_03: "#f9ebea",
};

const formatSeconds = (totalSeconds: number): string => {
  // Remove trailing numbers
  const roundedSeconds = Math.floor(totalSeconds);

  const hrs = Math.floor(roundedSeconds / 3600);
  const mins = Math.floor((roundedSeconds % 3600) / 60);
  const secs = roundedSeconds % 60;

  const hDisplay = hrs > 0 ? `${hrs.toString().padStart(2, "0")}:` : "";
  const mDisplay = mins.toString().padStart(2, "0");
  const sDisplay = secs.toString().padStart(2, "0");

  return `${hDisplay}${mDisplay}:${sDisplay}`;
};

interface RegistryHeaderProps {
  fetching: boolean;
  onRefresh: () => void;
}

const RegistryHeader = ({ fetching, onRefresh }: RegistryHeaderProps) => (
  <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center fw-bold py-2">
    <span>Transcription Registry</span>
    <button
      className="btn btn-sm btn-light d-flex align-items-center justify-content-center"
      onClick={onRefresh}
      disabled={fetching}
      title="Refresh Registry"
      style={{ width: "32px", height: "32px", borderRadius: "50%" }}
    >
      {fetching ? (
        <span className="spinner-border spinner-border-sm text-dark"></span>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          viewBox="0 0 16 16"
          className="bi bi-arrow-clockwise"
        >
          <path
            fillRule="evenodd"
            d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"
          />
          <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466" />
        </svg>
      )}
    </button>
  </div>
);

const LoadingState = () => (
  <div className="d-flex flex-column align-items-center justify-content-center p-5">
    <div className="spinner-grow text-primary mb-3" role="status"></div>
    <div className="text-muted fw-bold">LOADING...</div>
  </div>
);

interface JobRowProps {
  job: JobItem;
  isExpanded: boolean;
  onExpand: (jobId: string) => void;
  details: TranscriptionResponse | null;
  loading: boolean;
  audioUrl: string | null;
  loadingTranscriptionError: string | null;
  expandTrigger: number;
}

const JobRow = ({
  job,
  isExpanded,
  onExpand,
  details,
  loading,
  audioUrl,
  loadingTranscriptionError,
  expandTrigger,
}: JobRowProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const transcriptContainerRef = useRef<HTMLDivElement>(null);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [prevExpandTrigger, setPrevExpandTrigger] = useState(expandTrigger);

  // Reset playback state when anything is expanded (adjusted during render rather than in an effect)
  if (expandTrigger !== prevExpandTrigger) {
    setPrevExpandTrigger(expandTrigger);
    setPlaybackTime(0);
  }

  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    setPlaybackTime(e.currentTarget.currentTime);
  };

  // Helper to check if a segment is currently "active" in the audio
  const isSegmentActive = (start: number, end: number) => {
    return playbackTime >= start && playbackTime < end;
  };

  const jumpToTime = (seconds: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = seconds;
      void audioRef.current.play();
    }
  };

  const SPEAKER_COLOURS_LIST = Object.values(SPEAKER_COLOURS);

  const getSpeakerColour = (speaker?: string) => {
    if (!speaker) return "transparent";
    const index = parseInt(speaker.replace(/\D/g, ""), 10) % SPEAKER_COLOURS_LIST.length;
    return SPEAKER_COLOURS_LIST[index] ?? "#f0f0f0";
  };

  const formatSpeaker = (speaker: string) => {
    const match = /(\d+)$/.exec(speaker);
    if (!match) return speaker;
    return `Speaker ${(parseInt(match[1], 10) + 1).toString()}`;
  };

  // Centre 'subtitles' when audio playing
  useEffect(() => {
    const activeElem = transcriptContainerRef.current?.querySelector(".segment-active");
    if (activeElem) {
      activeElem.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  }, [playbackTime]);

  return (
    <React.Fragment>
      <tr>
        <td className="font-monospace small">
          <OverlayTrigger
            overlay={<Tooltip>{job.filename ?? job.job_id}</Tooltip>}
            placement="right"
          >
            <span>{(job.filename ?? job.job_id).slice(0, 8)}...</span>
          </OverlayTrigger>
        </td>
        <td>
          <span
            className={`badge ${
              job.status === "COMPLETED"
                ? "bg-success"
                : job.status === "FAILED"
                  ? "bg-danger"
                  : "bg-warning text-dark"
            }`}
          >
            {job.status}
          </span>
        </td>
        <td>
          <div className="small">
            <div className="fw-bold">{new Date(job.created_at).toLocaleDateString()}</div>
            <div className="text-muted" style={{ fontSize: "0.75rem" }}>
              {new Date(job.created_at).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          </div>
        </td>
        <td>
          {job.audio_duration !== undefined && job.audio_duration !== null
            ? formatSeconds(job.audio_duration)
            : "N/A"}
        </td>
        <td className="text-end">
          <button
            className="btn btn-sm btn-primary fw-bold"
            onClick={() => {
              onExpand(job.job_id);
            }}
            disabled={job.status !== "COMPLETED"}
          >
            {isExpanded ? "Collapse" : "Details"}
          </button>
        </td>
      </tr>

      {isExpanded && (
        <tr>
          <td colSpan={5} className="bg-white p-4 border-start border-4 border-primary">
            {loading && !details ? (
              <div className="text-center py-3 text-muted fw-bold italic">
                Retrieving transcripts...
              </div>
            ) : (
              <div className="row">
                <div className="col-md-4">
                  <h6 className="fw-bold">Audio Player</h6>
                  <audio
                    ref={audioRef}
                    src={audioUrl ?? ""}
                    controls
                    className="w-100 mb-3"
                    onTimeUpdate={handleTimeUpdate}
                  >
                    {/* The transcript alongside serves as the captions */}
                    <track kind="captions" />
                  </audio>
                  <div className="p-3 bg-dark text-success rounded small font-monospace">
                    ID: {job.job_id}
                    <br />
                    File: {job.filename ?? "Unknown"}
                  </div>
                </div>
                <div className="col-md-8">
                  <h6 className="fw-bold">Transcript (click to skip to corresponding audio)</h6>
                  {loadingTranscriptionError ? (
                    <div className="alert alert-danger fw-bold">
                      Transcript retrieval failed
                      {loadingTranscriptionError}
                    </div>
                  ) : (
                    <div
                      className="border rounded p-3 bg-white shadow-inner"
                      ref={transcriptContainerRef}
                      style={{ maxHeight: "300px", overflowY: "auto" }}
                    >
                      {details?.segments.map((seg: TranscriptSegment, i: number) => {
                        const active = isSegmentActive(seg.start, seg.end);
                        const showLabel =
                          i === 0 || seg.speaker !== details.segments[i - 1].speaker;
                        return (
                          <span key={i}>
                            {showLabel && seg.speaker && (
                              <span className="d-block mt-2 mb-1 fw-bold small text-muted">
                                {formatSpeaker(seg.speaker)}
                              </span>
                            )}
                            <span
                              className={`transcript-segment d-inline-block p-1 me-1 rounded ${active ? "segment-active" : ""}`}
                              style={{
                                cursor: "pointer",
                                backgroundColor: getSpeakerColour(seg.speaker),
                              }}
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                jumpToTime(seg.start);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  jumpToTime(seg.start);
                                }
                              }}
                              id={active ? "active-segment" : undefined}
                            >
                              {seg.text}
                            </span>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

interface JobRegistryProps {
  refreshTrigger: number;
}

export const JobRegistry = ({ refreshTrigger }: JobRegistryProps) => {
  const [fetchingJobs, setFetchingJobs] = useState(true);
  const [fetchingMoreJobs, setFetchingMoreJobs] = useState(true);
  const [jobs, setJobs] = useState<JobItem[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [expandTrigger, setExpandTrigger] = useState(0);

  const { loadJobDetails, loading, loadingTranscriptionError, details, audioUrl, reset } =
    useTranscription();

  const [prevRefreshTrigger, setPrevRefreshTrigger] = useState(refreshTrigger);

  const handleExpand = async (jobId: string) => {
    setExpandTrigger((t: number) => t + 1);
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
      return;
    }
    setExpandedJobId(jobId);
    await loadJobDetails(jobId);
  };

  // Only sets state in the .then callback, so it can be called from the fetch effect below
  // without synchronous setState. Loading flags / reset are handled by the callers
  const loadJobs = useCallback(
    (token: string | null) =>
      TranscriptionService.listJobs(10, token ?? undefined).then((data) => {
        if (token !== null) {
          setJobs((prev) => [...prev, ...data.items]);
        } else {
          setJobs(data.items);
        }
        setNextToken(data.next_token);
        setFetchingJobs(false);
        setFetchingMoreJobs(false);
      }),
    [],
  );

  const fetchJobs = (token: string | null = null) => {
    if (token === null) {
      setFetchingJobs(true);
    } else {
      setFetchingMoreJobs(true);
    }
    reset();
    void loadJobs(token);
  };

  // Parent bumped refreshTrigger: show loading and clear details (adjusted during render rather than in an effect)
  if (refreshTrigger !== prevRefreshTrigger) {
    setPrevRefreshTrigger(refreshTrigger);
    setFetchingJobs(true);
    reset();
  }

  useEffect(() => {
    void loadJobs(null);
  }, [refreshTrigger, loadJobs]);

  return (
    <div className="card shadow-sm border-0">
      <RegistryHeader
        fetching={fetchingJobs}
        onRefresh={() => {
          fetchJobs();
        }}
      />
      <div className="card-body p-0" style={{ minHeight: "200px", position: "relative" }}>
        {fetchingJobs && jobs.length === 0 ? (
          <LoadingState />
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-dark">
                <tr>
                  <th>Job ID</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Audio Duration</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <JobRow
                    key={job.job_id}
                    job={job}
                    isExpanded={expandedJobId === job.job_id}
                    onExpand={(jobId) => {
                      void handleExpand(jobId);
                    }}
                    details={details}
                    loading={loading}
                    audioUrl={audioUrl}
                    loadingTranscriptionError={loadingTranscriptionError}
                    expandTrigger={expandTrigger}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {nextToken && (
          <div className="card-footer bg-light text-center border-top py-3">
            <button
              className="btn btn-outline-secondary fw-bold px-5"
              onClick={() => {
                fetchJobs(nextToken);
              }}
              disabled={fetchingMoreJobs}
            >
              {fetchingMoreJobs ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Loading...
                </>
              ) : (
                "Load more transcripts"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
